import { useEffect, useState } from 'react';
import { scrapApi, num, SCRAP_REASON_LABEL, type ScrapEntry } from '@/api/scrap.api';
import { StatusBadge, SourceTag, inr, qtyText, dateText, DISPOSAL_LABEL } from './scrapUi';

// One scrap entry: where it came from, its valuation, its life cycle and the ledger vouchers it posted.

interface Props {
  entryId: string | null;
  canApprove: boolean;
  onClose: () => void;
  onApprove: (e: ScrapEntry) => void;
  onReject: (e: ScrapEntry) => void;
  onDispose: (e: ScrapEntry) => void;
  /** Bumped by the page after an action so the drawer reloads */
  version?: number;
}

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex justify-between gap-4 py-1.5 text-xs border-b border-[#f1f5f9] last:border-0">
    <span className="text-[#94a3b8] shrink-0">{label}</span>
    <span className="text-[#1e293b] text-right">{children}</span>
  </div>
);

export default function ScrapDetailDrawer({ entryId, canApprove, onClose, onApprove, onReject, onDispose, version = 0 }: Props) {
  const [entry, setEntry] = useState<ScrapEntry | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!entryId) return;
    let cancelled = false;
    setLoading(true);
    scrapApi.get(entryId)
      .then((r) => !cancelled && setEntry(r.data))
      .catch(() => !cancelled && setEntry(null))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [entryId, version]);

  if (!entryId) return null;

  const e = entry;
  const steps = e ? [
    { done: true, label: 'Raised', by: e.created_by_name, at: e.created_at },
    e.status === 'REJECTED'
      ? { done: true, label: 'Rejected', by: e.approved_by_name, at: e.approved_at, note: e.review_note }
      : { done: !!e.approved_at, label: 'Approved into scrap yard', by: e.approved_by_name, at: e.approved_at, note: e.review_note },
    ...(e.status === 'REJECTED' ? [] : [{
      done: e.status === 'DISPOSED',
      label: e.disposal_method ? DISPOSAL_LABEL[e.disposal_method] : 'Disposed',
      by: e.disposed_by_name,
      at: e.disposal_date,
    }]),
  ] : [];

  return (
    <div className="fixed inset-0 z-[550] flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white w-full max-w-md h-full shadow-2xl flex flex-col">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-[#e2e8f0]">
          <div>
            <p className="text-xs text-[#94a3b8]">Scrap entry</p>
            <h3 className="text-lg font-bold text-[#1e293b] font-mono">{e?.scrap_number || '…'}</h3>
            {e && <div className="flex items-center gap-2 mt-1"><StatusBadge status={e.status} method={e.disposal_method} /><SourceTag source={e.source_type} /></div>}
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-[#64748b] cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {loading && !e && <p className="text-sm text-[#94a3b8]"><i className="ri-loader-4-line animate-spin" /> Loading…</p>}
          {e && (
            <>
              <section>
                <h4 className="text-xs font-bold text-[#1e293b] uppercase tracking-wide mb-1">Scrap</h4>
                <Row label="Item">{e.item_code ? <span className="font-mono text-[#64748b] mr-1">{e.item_code}</span> : null}{e.item_name}</Row>
                <Row label="Quantity">{qtyText(e.qty)} {e.unit}</Row>
                <Row label="Reason">{SCRAP_REASON_LABEL[e.reason]}</Row>
                <Row label="Source">
                  {e.source_number || '—'}
                  {e.stage_number != null && ` · Stage ${e.stage_number}${e.stage_name ? ` (${e.stage_name})` : ''}`}
                  {e.ncr_number && <span className="block text-[#94a3b8]">from NCR {e.ncr_number}</span>}
                </Row>
                <Row label="Location">{e.scrap_location}{e.warehouse_name ? ` · ${e.warehouse_name}` : ''}</Row>
                <Row label="Date">{dateText(e.entry_date)}</Row>
                {e.notes && <Row label="Notes"><span className="whitespace-pre-line">{e.notes}</span></Row>}
              </section>

              <section>
                <h4 className="text-xs font-bold text-[#1e293b] uppercase tracking-wide mb-1">Value</h4>
                <Row label="Material cost (book)">{qtyText(e.qty)} × {inr(e.book_rate)} = <strong>{inr(e.book_value)}</strong></Row>
                <Row label="Scrap value">{qtyText(e.qty)} × {inr(e.scrap_rate)} = <strong>{inr(e.scrap_value)}</strong></Row>
                {e.status === 'DISPOSED' && e.disposal_method === 'SOLD' && (
                  <>
                    <Row label="Sold to">{e.buyer_name}</Row>
                    <Row label="Sale amount">{inr(e.sale_amount)}{num(e.gst_amount) > 0 && ` + GST ${num(e.gst_rate)}% ${inr(e.gst_amount)}`}</Row>
                    <Row label="Received in">{e.payment_mode === 'CASH' ? 'Cash' : 'Bank'}{e.disposal_reference ? ` · ${e.disposal_reference}` : ''}</Row>
                  </>
                )}
                {num(e.disposal_cost) > 0 && <Row label="Disposal cost">{inr(e.disposal_cost)}</Row>}
                {(e.status === 'APPROVED' || e.status === 'DISPOSED') && (
                  <>
                    {e.recovery > 0 && <Row label="Recovered"><span className="text-green-700 font-semibold">{inr(e.recovery)}</span></Row>}
                    <Row label={e.source_type === 'PROCESS' ? 'P&L loss (process waste is part of the order cost)' : 'P&L loss so far'}>
                      <span className={e.loss > 0 ? 'text-red-600 font-semibold' : 'text-green-700 font-semibold'}>{e.loss >= 0 ? inr(e.loss) : `Profit ${inr(-e.loss)}`}</span>
                    </Row>
                  </>
                )}
              </section>

              <section>
                <h4 className="text-xs font-bold text-[#1e293b] uppercase tracking-wide mb-2">Life cycle</h4>
                <ol className="space-y-2.5">
                  {steps.map((s, i) => (
                    <li key={i} className="flex gap-2.5">
                      <i className={`${s.done ? 'ri-checkbox-circle-fill text-[#4f46e5]' : 'ri-checkbox-blank-circle-line text-[#cbd5e1]'} text-base leading-none mt-px`} />
                      <div className="text-xs">
                        <p className={s.done ? 'font-semibold text-[#1e293b]' : 'text-[#94a3b8]'}>{s.label}</p>
                        {s.done && <p className="text-[#94a3b8]">{s.by || '—'} · {dateText(s.at)}</p>}
                        {'note' in s && s.note && <p className="text-[#64748b] italic">“{s.note}”</p>}
                      </div>
                    </li>
                  ))}
                </ol>
              </section>

              {(e.vouchers?.length ?? 0) > 0 && (
                <section>
                  <h4 className="text-xs font-bold text-[#1e293b] uppercase tracking-wide mb-1">Ledger postings</h4>
                  {e.vouchers!.map((v) => (
                    <div key={v.id} className="py-1.5 text-xs border-b border-[#f1f5f9] last:border-0">
                      <div className="flex justify-between">
                        <span className="font-mono text-[#4f46e5]">{v.voucher_number}</span>
                        <span className="font-semibold">{inr(v.total_debit)}</span>
                      </div>
                      <p className="text-[#94a3b8]">{v.voucher_type === 'SCRAP_DISPOSAL' ? 'Disposal' : 'Into scrap stock'} · {dateText(v.voucher_date)}</p>
                    </div>
                  ))}
                </section>
              )}
            </>
          )}
        </div>

        {e && canApprove && (e.status === 'PENDING' || e.status === 'APPROVED') && (
          <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-[#e2e8f0] bg-[#f8fafc]">
            {e.status === 'PENDING' && (
              <>
                <button type="button" onClick={() => onReject(e)} className="h-9 px-3 rounded-lg border border-red-200 text-sm font-medium text-red-600 hover:bg-red-50 cursor-pointer">Reject</button>
                <button type="button" onClick={() => onApprove(e)} className="h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 cursor-pointer">Approve</button>
              </>
            )}
            {e.status === 'APPROVED' && (
              <button type="button" onClick={() => onDispose(e)} className="h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 cursor-pointer">
                <i className="ri-hand-coin-line" /> Dispose
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
